import type { RenderData } from '../assetTypes'

export const ASSET_REFERENCE_REGEX = /asset:\/\/([0-9a-fA-F-]{36})/g

export const normalizeAssetId = (value: string) => value.trim().toLowerCase()

export const extractAssetReferences = (value: string, target: Set<string>) => {
  if (!value) return
  for (const match of value.matchAll(ASSET_REFERENCE_REGEX)) {
    const id = match[1]
    if (id) target.add(normalizeAssetId(id))
  }
}

export const collectAssetReferencesFromText = (content: string) => {
  const matches = new Set<string>()
  extractAssetReferences(content, matches)
  return Array.from(matches)
}

export const collectAssetReferencesFromRenderData = (content: string, renderData?: RenderData) => {
  const matches = new Set<string>()
  extractAssetReferences(content, matches)
  renderData?.entity?.fields?.forEach((field) => {
    if (typeof field.value === 'string') extractAssetReferences(field.value, matches)
  })
  return Array.from(matches)
}

export type AssetReferenceCollector = (content: string, renderData?: RenderData) => string[]

export type AssetReferenceResolver = (assetId: string) => Promise<{
  content: string
  renderData?: RenderData
  collectAssetReferences?: AssetReferenceCollector
} | null>

export type CollectAssetReferencesRecursiveOptions = {
  content: string
  renderData?: RenderData
  collectAssetReferences?: AssetReferenceCollector
  resolveAsset: AssetReferenceResolver
  maxDepth?: number
}

export const collectAssetReferencesRecursive = async ({
  content,
  renderData,
  collectAssetReferences = collectAssetReferencesFromRenderData,
  resolveAsset,
  maxDepth = 3,
}: CollectAssetReferencesRecursiveOptions) => {
  const result = new Set<string>()
  const visited = new Set<string>()
  const queue: Array<{
    content: string
    renderData?: RenderData
    collectAssetReferences?: AssetReferenceCollector
    depth: number
  }> = [{ content, renderData, collectAssetReferences, depth: 0 }]

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue
    const collector = current.collectAssetReferences ?? collectAssetReferencesFromRenderData
    const references = collector(current.content, current.renderData)
    for (const reference of references) {
      if (visited.has(reference)) continue
      visited.add(reference)
      result.add(reference)
      if (current.depth >= maxDepth) continue
      const resolved = await resolveAsset(reference)
      if (!resolved) continue
      queue.push({
        content: resolved.content,
        renderData: resolved.renderData,
        collectAssetReferences: resolved.collectAssetReferences,
        depth: current.depth + 1,
      })
    }
  }

  return Array.from(result)
}
